import { useState, useEffect } from 'react';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate } from '../api/templates';
import { getCompanies } from '../api/companies';
import { Topbar } from '../layout/Topbar';
import type { MessageTemplate, Company } from '../types';

export function TemplatesPage() {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('');
  const [content, setContent] = useState('');
  const [department, setDepartment] = useState('');
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    getCompanies().then(setCompanies).catch(() => console.error('Falha ao carregar empresas'));
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplates(filterCompanyId || undefined);
      setTemplates(data);
    } catch (err) { console.error('Falha ao carregar templates:', err); } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [filterCompanyId]);

  const handleSelect = (tpl: MessageTemplate) => {
    setSelectedTemplate(tpl);
    setName(tpl.name);
    setTrigger(tpl.trigger);
    setContent(tpl.content);
    setDepartment(tpl.departmentType || '');
    setCompanyId(tpl.companyId);
  };

  const handleNew = () => {
    setSelectedTemplate(null);
    setName('');
    setTrigger('');
    setContent('');
    setDepartment('');
    setCompanyId(companies[0]?.id || '');
  };

  const handleSave = async () => {
    if (!name || !trigger || !content) return;
    try {
      if (selectedTemplate) {
        const updated = await updateTemplate(selectedTemplate.id, {
          name, trigger, content,
          departmentType: department || undefined,
        });
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await createTemplate({
          name, trigger, content,
          departmentType: department || undefined,
          companyId,
        });
        setTemplates((prev) => [...prev, created]);
      }
      handleNew();
    } catch (err) {
      console.error('Falha ao salvar template:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteTemplate(selectedTemplate.id);
      setTemplates((prev) => prev.filter((t) => t.id !== selectedTemplate.id));
      handleNew();
    } catch (err) {
      console.error('Falha ao excluir template:', err);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Templates" subtitle="Mensagens automáticas do bot de atendimento." />

      <div className="records-layout">
        <div className="panel">
          <div className="panel-header">
            <h2>Templates</h2>
            <div className="flex gap-sm">
              <select value={filterCompanyId} onChange={(e) => setFilterCompanyId(e.target.value)} style={{ width: 'auto' }}>
                <option value="">Todas empresas</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={handleNew}>+ Novo</button>
            </div>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : templates.length === 0 ? (
            <div className="empty-state"><p>Nenhum template encontrado.</p></div>
          ) : (
            <div className="data-list">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="data-list-item"
                  onClick={() => handleSelect(tpl)}
                  style={{ background: selectedTemplate?.id === tpl.id ? 'var(--color-primary-bg)' : undefined }}
                >
                  <div>
                    <div className="item-name">{tpl.name}</div>
                    <div className="item-detail">
                      Gatilho: "{tpl.trigger}" {tpl.departmentType ? `• ${tpl.departmentType}` : ''}
                    </div>
                  </div>
                  <span className={`pill ${tpl.isActive ? 'success' : 'neutral'}`}>
                    {tpl.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>{selectedTemplate ? 'Editar' : 'Novo'} template</h2>
          </div>
          <div className="panel-body">
            <div className="form-stack">
              <label>
                Empresa
                <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label>
                Nome
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Boas-vindas" />
              </label>
              <label>
                Gatilho
                <input value={trigger} onChange={(e) => setTrigger(e.target.value)} placeholder="Ex: /start" />
              </label>
              <label>
                Setor
                <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                  <option value="">Geral</option>
                  <option value="FINANCEIRO">Financeiro</option>
                  <option value="SUPORTE_TECNICO">Suporte Técnico</option>
                  <option value="VENDAS">Vendas</option>
                  <option value="ADMINISTRATIVO">Administrativo</option>
                </select>
              </label>
              <label>
                Mensagem
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  placeholder="Use {{cliente}}, {{empresa}}, {{protocolo}}, {{setor}} como variáveis"
                />
              </label>
              <div className="flex gap-sm">
                <button onClick={handleSave}>Salvar</button>
                <button className="secondary" onClick={handleNew}>Cancelar</button>
                {selectedTemplate && <button className="danger" onClick={handleDelete}>Excluir</button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
